"use client";

import Link from "next/link";
import { useState } from "react";
import { useAuth } from "@/components/AuthProvider";
import { cardnexusConnectUrl, cardnexusFetch } from "@/lib/cardnexus/browser";
import { importDeckListToSavedDeck } from "@/lib/deck-import";
import type { SavedDeck } from "@/lib/local-store";
import type { CardRecord } from "@/lib/types";

type ListSummary = {
  id: string;
  name: string;
  status: "forSale" | "toComplete" | "hold";
  bannerUrl: string | null;
  completionPercentage: number;
  totalQuantity: number;
};

const STATUS_LABEL: Record<ListSummary["status"], string> = {
  toComplete: "À compléter",
  hold: "De côté",
  forSale: "En vente",
};

/** Importe une liste CardNexus (deck) dans les decks RiftHub. */
export default function CardNexusDeckImport({
  cards,
  onImported,
  returnTo = "/decks",
}: {
  cards: CardRecord[];
  onImported: (deck: SavedDeck, unresolved: string[]) => void;
  returnTo?: string;
}) {
  const { user } = useAuth();
  const [lists, setLists] = useState<ListSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [needsLink, setNeedsLink] = useState(false);

  if (!user) {
    return (
      <p className="text-xs text-gray-500">
        <Link href="/compte" className="text-cyan-300 underline">
          Connecte-toi
        </Link>{" "}
        et lie CardNexus pour importer tes decks depuis tes listes CardNexus.
      </p>
    );
  }

  async function loadLists() {
    setLoading(true);
    setError(null);
    setNeedsLink(false);
    try {
      const data = await cardnexusFetch<{ lists: ListSummary[] }>("/api/cardnexus/lists");
      setLists(data.lists ?? []);
    } catch (e) {
      const err = e as Error & { status?: number };
      if (err.status === 401) setNeedsLink(true);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function importList(list: ListSummary) {
    setImportingId(list.id);
    setError(null);
    try {
      const data = await cardnexusFetch<{ decklist: string; unresolved: string[]; name: string }>(
        `/api/cardnexus/lists/${encodeURIComponent(list.id)}`,
      );
      if (!data.decklist.trim()) throw new Error("Cette liste CardNexus est vide.");
      const { deck, unresolved } = importDeckListToSavedDeck(data.decklist, cards, { name: data.name });
      onImported(deck, [...new Set([...data.unresolved, ...unresolved])]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import impossible.");
    } finally {
      setImportingId(null);
    }
  }

  return (
    <div className="space-y-3">
      {lists === null ? (
        <button
          type="button"
          onClick={() => void loadLists()}
          disabled={loading || cards.length === 0}
          className="rounded-xl border border-cyan-400/30 bg-cyan-500/15 px-4 py-2.5 text-sm font-bold text-cyan-200 hover:bg-cyan-500/25 disabled:opacity-50"
        >
          {loading ? "Chargement…" : "Importer depuis mes listes CardNexus"}
        </button>
      ) : lists.length === 0 ? (
        <p className="text-sm text-gray-400">Aucune liste Riftbound sur ton compte CardNexus.</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {lists.map((l) => (
            <button
              key={l.id}
              type="button"
              disabled={!!importingId}
              onClick={() => void importList(l)}
              className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/30 p-2.5 text-left transition hover:border-cyan-400/50 disabled:opacity-60"
            >
              {l.bannerUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={l.bannerUrl} alt="" className="h-11 w-11 rounded-lg object-cover" />
              ) : (
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-white/5 text-gray-500">🎴</div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{l.name}</p>
                <p className="text-[11px] text-gray-500">
                  {l.totalQuantity} cartes · {STATUS_LABEL[l.status] ?? l.status} ·{" "}
                  {Math.round(l.completionPercentage)}% possédé
                </p>
              </div>
              <span className="text-xs font-bold text-cyan-300">{importingId === l.id ? "…" : "Importer"}</span>
            </button>
          ))}
        </div>
      )}
      {error && <p className="text-sm text-red-300">{error}</p>}
      {needsLink && (
        <a href={cardnexusConnectUrl(returnTo)} className="inline-flex rounded-xl bg-cyan-600 px-4 py-2 text-sm font-bold hover:bg-cyan-500">
          Connecter CardNexus
        </a>
      )}
    </div>
  );
}
